import { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { TrendingUp, Cpu, AlertTriangle, X, Search, Filter, ArrowUpDown, ChevronLeft, ChevronRight } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Legend,
  BarChart, Bar
} from "recharts";
import { MultiSelect } from "@/components/ui/multi-select";

const COLORS = ['#006994', '#EF4444', '#F97316', '#8B5CF6', '#06B6D4', '#10B981', '#6366F1', '#EC4899', '#14B8A6', '#F59E0B', '#84CC16', '#A855F7', '#0EA5E9'];
const ROOT_CAUSE_COLORS = ['#EF4444', '#F97316', '#8B5CF6', '#06B6D4', '#10B981', '#6366F1', '#94A3B8'];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatShortDate = (value) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");
  return m ? `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}` : value;
};

export default function AdvancedAnalytics() {
  const [rootCauseData, setRootCauseData] = useState([]);
  const [rootCauseLoading, setRootCauseLoading] = useState(true);
  const [stickSeriesData, setStickSeriesData] = useState([]);
  const [stickSeriesLoading, setStickSeriesLoading] = useState(true);
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [selectedRootCause, setSelectedRootCause] = useState(null);

  const [filters, setFilters] = useState({
    ddCode: "",
    states: [],
    pas: [],
    fas: [],
    cities: [],
    dmas: [],
    stickSeries: [],
    dateFrom: "",
    dateTo: ""
  });
  const [filterOptions, setFilterOptions] = useState({
    states: [],
    pas: [],
    fas: [],
    cities: [],
    dmas: [],
    stickSeries: []
  });
  const [showFilters, setShowFilters] = useState(false);
  
  const [ddCodeSuggestions, setDdCodeSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchingDDCode, setSearchingDDCode] = useState(false);

  const [recurringVenues, setRecurringVenues] = useState([]);
  const [recurringLoading, setRecurringLoading] = useState(true);
  const [recurringSort, setRecurringSort] = useState({ field: "days_since_fixed", direction: "asc" });
  const [recurringSearch, setRecurringSearch] = useState("");
  const [recurringPage, setRecurringPage] = useState(1);
  const recurringPageSize = 15;
  const [recurringFilters, setRecurringFilters] = useState({ pa: [], fa: [], state: [], city: [] });
  const [recurringFilterOptions, setRecurringFilterOptions] = useState({ pa: [], fa: [], state: [], city: [] });

  const [categoryTrend, setCategoryTrend] = useState([]);
  const [categoryNames, setCategoryNames] = useState([]);
  const [categoryTrendLoading, setCategoryTrendLoading] = useState(true);
  const [mttrStats, setMttrStats] = useState(null);
  const [mttrLoading, setMttrLoading] = useState(true);
  const [installerTrend, setInstallerTrend] = useState([]);
  const [installerNames, setInstallerNames] = useState([]);
  const [installerTrendLoading, setInstallerTrendLoading] = useState(true);
  const [installerDays, setInstallerDays] = useState(30);
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setInstallerTrendLoading(true);
    (async () => {
      try {
        const res = await axios.get(`${API}/installer-daily-trend?days=${installerDays}`, { withCredentials: true });
        if (cancelled) return;
        setInstallerTrend(res.data?.trend || []);
        setInstallerNames(res.data?.installers || []);
      } catch (err) {
        console.error("Error fetching installer daily trend:", err);
      } finally {
        if (!cancelled) setInstallerTrendLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [installerDays]);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const [filtersRes, offlineRes] = await Promise.all([
          axios.get(`${API}/advanced-analytics-filters`, { withCredentials: true }),
          axios.get(`${API}/offline-analytics/dates`, { withCredentials: true }).then(async (datesRes) => {
            const latestDate = datesRes.data?.dates?.[0];
            if (!latestDate) return { data: { data: [] } };
            return axios.get(`${API}/offline-analytics?date=${latestDate}`, { withCredentials: true });
          }).catch(() => ({ data: { data: [] } }))
        ]);
        
        const rows = offlineRes.data?.data || [];
        const dmaSet = new Set();
        const ssSet = new Set();
        rows.forEach(r => {
          if (r.DMA) dmaSet.add(r.DMA);
          if (r["Stick Series"]) ssSet.add(r["Stick Series"]);
        });
        
        setFilterOptions({
          states: filtersRes.data.states || [],
          pas: filtersRes.data.pas || [],
          fas: filtersRes.data.fas || [],
          cities: filtersRes.data.cities || [],
          dmas: [...dmaSet].sort(),
          stickSeries: [...ssSet].sort()
        });
      } catch (error) {
        console.error("Error fetching filter options:", error);
      }
    };
    fetchFilterOptions();
  }, []);

  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    if (filters.ddCode) params.append("dd_code", filters.ddCode);
    if (filters.states.length > 0) params.append("state", filters.states.join(","));
    if (filters.pas.length > 0) params.append("pa", filters.pas.join(","));
    if (filters.fas.length > 0) params.append("fa", filters.fas.join(","));
    if (filters.cities.length > 0) params.append("city", filters.cities.join(","));
    if (filters.dateFrom) params.append("date_from", filters.dateFrom);
    if (filters.dateTo) params.append("date_to", filters.dateTo);
    (filters.dmas || []).forEach(d => params.append("dma", d));
    (filters.stickSeries || []).forEach(s => params.append("stick_series", s));
    return params.toString();
  }, [filters]);

  useEffect(() => {
    const fetchRootCause = async () => {
      setRootCauseLoading(true);
      try {
        const url = queryParams 
          ? `${API}/root-cause-breakdown?${queryParams}` 
          : `${API}/root-cause-breakdown`;
        const response = await axios.get(url, { withCredentials: true });
        setRootCauseData(response.data.breakdown || []);
      } catch (error) {
        console.error("Error fetching root cause data:", error);
      } finally {
        setRootCauseLoading(false);
      }
    };
    fetchRootCause();
  }, [queryParams]);

  useEffect(() => {
    const fetchStickSeries = async () => {
      setStickSeriesLoading(true);
      try {
        const url = queryParams 
          ? `${API}/stick-series-breakdown?${queryParams}` 
          : `${API}/stick-series-breakdown`;
        const response = await axios.get(url, { withCredentials: true });
        setStickSeriesData(response.data.breakdown || []);
      } catch (error) {
        console.error("Error fetching stick series data:", error);
      } finally {
        setStickSeriesLoading(false);
      }
    };
    fetchStickSeries();
  }, [queryParams]);

  useEffect(() => {
    const fetchCategoryTrend = async () => {
      setCategoryTrendLoading(true);
      try {
        const response = await axios.get(`${API}/category-trend`, { withCredentials: true });
        setCategoryTrend(response.data.trend || []);
        setCategoryNames(response.data.categories || []);
      } catch (error) {
        console.error("Error fetching category trend:", error);
      } finally {
        setCategoryTrendLoading(false);
      }
    };
    fetchCategoryTrend();
  }, []);

  useEffect(() => {
    const fetchMttr = async () => {
      setMttrLoading(true);
      try {
        const response = await axios.get(`${API}/mttr-stats`, { withCredentials: true });
        setMttrStats(response.data);
      } catch (error) {
        console.error("Error fetching MTTR stats:", error);
      } finally {
        setMttrLoading(false);
      }
    };
    fetchMttr();
  }, []);

  useEffect(() => {
    const fetchRecurringVenues = async () => {
      setRecurringLoading(true);
      try {
        const [logsRes, offlineRes] = await Promise.all([
          axios.get(`${API}/recurring-venues`, { withCredentials: true }),
          axios.get(`${API}/offline-analytics`, {
            params: { date: 'latest', status: 'Offline' },
            withCredentials: true,
          }),
        ]);

        const logVenues = logsRes.data.venues || {};
        const latestDateRaw = offlineRes.data.date || '';
        let latestDateISO = '';
        if (latestDateRaw) {
          const parts = latestDateRaw.split('-');
          if (parts.length === 3) {
            latestDateISO = `${parts[2]}-${parts[0]}-${parts[1]}`;
          }
        }

        const csvLookup = {};
        (offlineRes.data.data || []).forEach(row => {
          const venueId = row['Venue ID'];
          if (!venueId) return;
          csvLookup[venueId] = {
            dd_code: venueId,
            storeName: row['Store Name'] || '',
            address: row['Address'] || '',
            city: row['City'] || '',
            state: row['State'] || '',
            pa: row['PA Title'] || '',
            fa: row['FA Title'] || '',
          };
        });

        const merged = [];
        const seenDDCodes = new Set();
        const paSet = new Set();
        const faSet = new Set();
        const stateSet = new Set();
        const citySet = new Set();
        Object.keys(logVenues).forEach(ddCode => {
          if (seenDDCodes.has(ddCode)) return;
          const csv = csvLookup[ddCode];
          const log = logVenues[ddCode];
          if (csv && log.fixed_date) {
            const fixedDate = log.fixed_date;
            const offlineDate = new Date(latestDateISO);
            const fixed = new Date(fixedDate);
            const daysSinceFixed = Math.round((offlineDate - fixed) / (1000 * 60 * 60 * 24));

            if (daysSinceFixed <= 0) return;

            if (csv.pa) paSet.add(csv.pa);
            if (csv.fa) faSet.add(csv.fa);
            if (csv.state) stateSet.add(csv.state);
            if (csv.city) citySet.add(csv.city);

            seenDDCodes.add(ddCode);
            merged.push({
              dd_code: ddCode,
              store_name: csv.storeName,
              address: csv.address,
              city: csv.city,
              state: csv.state,
              pa: csv.pa,
              fa: csv.fa,
              category: log.category || '',
              recent_offline_date: latestDateISO,
              fixed_date: fixedDate,
              days_since_fixed: daysSinceFixed,
              log_count: log.log_count
            });
          }
        });

        setRecurringFilterOptions({
          pa: [...paSet].sort(),
          fa: [...faSet].sort(),
          state: [...stateSet].sort(),
          city: [...citySet].sort()
        });
        setRecurringVenues(merged);
        setRecurringLoading(false);
      } catch (error) {
        console.error("Error fetching recurring venues:", error);
        setRecurringLoading(false);
      }
    };
    fetchRecurringVenues();
  }, []);

  const processedRecurringVenues = useMemo(() => {
    let data = [...recurringVenues];
    
    if (recurringFilters.pa.length > 0) {
      data = data.filter(v => recurringFilters.pa.includes(v.pa));
    }
    if (recurringFilters.fa.length > 0) {
      data = data.filter(v => recurringFilters.fa.includes(v.fa));
    }
    if (recurringFilters.state.length > 0) {
      data = data.filter(v => recurringFilters.state.includes(v.state));
    }
    if (recurringFilters.city.length > 0) {
      data = data.filter(v => recurringFilters.city.includes(v.city));
    }
    
    if (recurringSearch) {
      const q = recurringSearch.toLowerCase();
      data = data.filter(v => 
        v.dd_code.toLowerCase().includes(q) ||
        v.store_name.toLowerCase().includes(q) ||
        v.address.toLowerCase().includes(q) ||
        v.city.toLowerCase().includes(q) ||
        v.state.toLowerCase().includes(q) ||
        v.pa.toLowerCase().includes(q) ||
        v.fa.toLowerCase().includes(q)
      );
    }
    
    data.sort((a, b) => {
      const field = recurringSort.field;
      const dir = recurringSort.direction === "asc" ? 1 : -1;
      if (typeof a[field] === "number") return (a[field] - b[field]) * dir;
      return String(a[field] || '').localeCompare(String(b[field] || '')) * dir;
    });
    
    return data;
  }, [recurringVenues, recurringSearch, recurringSort, recurringFilters]);

  const recurringTotalPages = Math.max(1, Math.ceil(processedRecurringVenues.length / recurringPageSize));
  const paginatedRecurringVenues = processedRecurringVenues.slice(
    (recurringPage - 1) * recurringPageSize,
    recurringPage * recurringPageSize
  );

  const toggleRecurringSort = (field) => {
    setRecurringSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === "asc" ? "desc" : "asc"
    }));
    setRecurringPage(1);
  };

  const recurringKPIs = useMemo(() => {
    const under30 = recurringVenues.filter(v => v.days_since_fixed < 30);
    
    const categoryCounts = {};
    under30.forEach(v => {
      if (v.category) {
        categoryCounts[v.category] = (categoryCounts[v.category] || 0) + 1;
      }
    });
    
    const categoryBreakdown = Object.entries(categoryCounts)
      .map(([cat, count]) => ({
        name: cat,
        count,
        percentage: under30.length > 0 ? Math.round((count / under30.length) * 100) : 0
      }))
      .sort((a, b) => b.count - a.count);
    
    return {
      offlineUnder30: under30.length,
      totalRecurring: recurringVenues.length,
      categoryBreakdown,
    };
  }, [recurringVenues]);

  const clearFilters = () => {
    setFilters({
      ddCode: "",
      states: [],
      pas: [],
      fas: [],
      cities: [],
      dmas: [],
      stickSeries: [],
      dateFrom: "",
      dateTo: ""
    });
  };

  const hasActiveFilters = filters.ddCode || filters.states.length > 0 || filters.pas.length > 0 || filters.fas.length > 0 || filters.cities.length > 0 || (filters.dmas && filters.dmas.length > 0) || (filters.stickSeries && filters.stickSeries.length > 0) || filters.dateFrom || filters.dateTo;

  const RootCauseTooltip = ({ active, payload }) => {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      return (
        <div className="bg-white rounded-lg border border-slate-200 shadow-lg p-4 max-w-sm">
          <p className="font-semibold text-slate-900 mb-1 text-base">{data.name}</p>
          <p className="text-sm text-slate-600 mb-2">
            {data.count} logs ({data.percentage}%)
          </p>
          <p className="text-xs text-slate-400">Click for full breakdown</p>
        </div>
      );
    }
    return null;
  };

  const StickSeriesTooltip = ({ active, payload }) => {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      return (
        <div className="bg-white rounded-lg border border-slate-200 shadow-lg p-4 max-w-sm">
          <p className="font-semibold text-slate-900 mb-1 text-base">{data.name} Series</p>
          <p className="text-sm text-slate-600 mb-2">
            {data.count} logs ({data.percentage}%)
          </p>
          <p className="text-xs text-slate-400">Click for full breakdown</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="p-8 max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <TrendingUp className="w-7 h-7 text-[#006994]" />
            Advanced Analytics
          </h1>
          <p className="text-slate-500 mt-1">Deep insights from service logs database</p>
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          data-testid="aa-toggle-filters-btn"
          className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
            showFilters || hasActiveFilters
              ? 'bg-[#006994] text-white border-[#006994]'
              : 'bg-white text-slate-700 border-slate-200 hover:border-[#006994]'
          }`}
        >
          <Filter className="w-4 h-4" />
          Filters
          {hasActiveFilters && (
            <span className="bg-white text-[#006994] text-xs px-1.5 py-0.5 rounded-full font-medium">
              {filters.states.length + filters.pas.length + filters.fas.length + filters.cities.length + (filters.dmas || []).length + (filters.stickSeries || []).length + (filters.ddCode ? 1 : 0) + (filters.dateFrom ? 1 : 0) + (filters.dateTo ? 1 : 0)}
            </span>
          )}
        </button>
      </div>

      {showFilters && (
        <div className="dolphin-card p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-700">Filter Analytics Data</h3>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-sm text-red-500 hover:text-red-600 flex items-center gap-1"
              >
                <X className="w-4 h-4" />
                Clear All
              </button>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">DD Code / Store Name</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search DD Code..."
                  value={filters.ddCode}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFilters(prev => ({ ...prev, ddCode: value }));
                    
                    if (value.length >= 2) {
                      setSearchingDDCode(true);
                      axios.get(`${API}/logs/search?q=${encodeURIComponent(value)}`, { withCredentials: true })
                        .then(res => {
                          setDdCodeSuggestions(res.data || []);
                          setShowSuggestions(true);
                        })
                        .catch(() => setDdCodeSuggestions([]))
                        .finally(() => setSearchingDDCode(false));
                    } else {
                      setDdCodeSuggestions([]);
                      setShowSuggestions(false);
                    }
                  }}
                  onFocus={() => {
                    if (ddCodeSuggestions.length > 0) setShowSuggestions(true);
                  }}
                  onBlur={() => {
                    setTimeout(() => setShowSuggestions(false), 200);
                  }}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006994] focus:border-[#006994]"
                />
                {searchingDDCode && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="w-4 h-4 border-2 border-slate-200 border-t-[#006994] rounded-full animate-spin"></div>
                  </div>
                )}
                
                {showSuggestions && ddCodeSuggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {ddCodeSuggestions.map((suggestion, idx) => (
                      <div
                        key={idx}
                        className="px-3 py-2 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-b-0"
                        onClick={() => {
                          setFilters(prev => ({ ...prev, ddCode: suggestion.dd_code }));
                          setShowSuggestions(false);
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-slate-900 text-sm">{suggestion.dd_code}</span>
                          <span className="text-xs text-slate-400">{suggestion.log_count} logs</span>
                        </div>
                        {suggestion.venue_name && (
                          <p className="text-xs text-slate-500 truncate">{suggestion.venue_name}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">State</label>
              <MultiSelect
                options={filterOptions.states}
                selected={filters.states}
                onChange={(values) => setFilters(prev => ({ ...prev, states: values }))}
                placeholder="All States"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">City</label>
              <MultiSelect
                options={filterOptions.cities}
                selected={filters.cities}
                onChange={(values) => setFilters(prev => ({ ...prev, cities: values }))}
                placeholder="All Cities"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">PA</label>
              <MultiSelect
                options={filterOptions.pas}
                selected={filters.pas}
                onChange={(values) => setFilters(prev => ({ ...prev, pas: values }))}
                placeholder="All PAs"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">FA</label>
              <MultiSelect
                options={filterOptions.fas}
                selected={filters.fas}
                onChange={(values) => setFilters(prev => ({ ...prev, fas: values }))}
                placeholder="All FAs"
              />
            </div>

            {/* DMA Filter */}
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">DMA</label>
              <MultiSelect
                options={filterOptions.dmas || []}
                selected={filters.dmas || []}
                onChange={(values) => setFilters(prev => ({ ...prev, dmas: values }))}
                placeholder="All DMAs"
                testId="aa-dma-filter"
              />
            </div>

            {/* Stick Series Filter */}
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">Stick Series</label>
              <MultiSelect
                options={filterOptions.stickSeries || []}
                selected={filters.stickSeries || []}
                onChange={(values) => setFilters(prev => ({ ...prev, stickSeries: values }))}
                placeholder="All Stick Series"
                testId="aa-stick-series-filter"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">Date From</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006994] focus:border-[#006994]"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">Date To</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006994] focus:border-[#006994]"
              />
            </div>
          </div>
        </div>
      )}

      {hasActiveFilters && !showFilters && (
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <span className="text-sm text-slate-500">Active filters:</span>
          {filters.ddCode && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs">
              DD: {filters.ddCode}
              <X className="w-3 h-3 cursor-pointer" onClick={() => setFilters(prev => ({ ...prev, ddCode: "" }))} />
            </span>
          )}
          {filters.states.map(state => (
            <span key={state} className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded-full text-xs">
              {state}
              <X className="w-3 h-3 cursor-pointer" onClick={() => setFilters(prev => ({ ...prev, states: prev.states.filter(s => s !== state) }))} />
            </span>
          ))}
          {filters.pas.map(pa => (
            <span key={pa} className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 rounded-full text-xs truncate max-w-[200px]">
              {pa}
              <X className="w-3 h-3 cursor-pointer shrink-0" onClick={() => setFilters(prev => ({ ...prev, pas: prev.pas.filter(p => p !== pa) }))} />
            </span>
          ))}
          {filters.fas.map(fa => (
            <span key={fa} className="inline-flex items-center gap-1 px-2 py-1 bg-orange-50 text-orange-700 rounded-full text-xs truncate max-w-[200px]">
              {fa}
              <X className="w-3 h-3 cursor-pointer shrink-0" onClick={() => setFilters(prev => ({ ...prev, fas: prev.fas.filter(f => f !== fa) }))} />
            </span>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="dolphin-card p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Root Cause Breakdown
              </h3>
              <p className="text-sm text-slate-500 mt-1">Click on legend to see category details</p>
            </div>
          </div>
          <div className="h-[350px]">
            {rootCauseLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="w-10 h-10 border-2 border-slate-200 border-t-[#006994] rounded-full animate-spin"></div>
              </div>
            ) : rootCauseData.length === 0 ? (
              <div className="flex items-center justify-center h-full text-slate-500">
                <p>No data available for selected filters</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={rootCauseData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="count"
                    nameKey="name"
                    isAnimationActive={false}
                    onClick={(data) => setSelectedRootCause(data)}
                    label={({ name, percentage }) => percentage >= 4 ? `${name} ${percentage}%` : ''}
                    labelLine={({ percentage }) => percentage >= 4}
                  >
                    {rootCauseData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={ROOT_CAUSE_COLORS[index % ROOT_CAUSE_COLORS.length]}
                        style={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<RootCauseTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 mt-4 pt-4 border-t border-slate-100">
            {rootCauseData.map((item, idx) => (
              <div 
                key={idx} 
                className="flex items-center gap-2 text-sm cursor-pointer hover:bg-red-50 p-1.5 rounded transition-colors"
                onClick={() => setSelectedRootCause(item)}
              >
                <div 
                  className="w-3 h-3 rounded-full shrink-0" 
                  style={{ backgroundColor: ROOT_CAUSE_COLORS[idx % ROOT_CAUSE_COLORS.length] }}
                ></div>
                <span className="text-slate-600 truncate">{item.name}</span>
                <span className="text-slate-900 font-medium ml-auto">{item.percentage}%</span>
              </div>
            ))}
          </div>
        </div>

        <div className="dolphin-card p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Stick Series Distribution
              </h3>
              <p className="text-sm text-slate-500 mt-1">Stick series and problem categories</p>
            </div>
          </div>
          <div className="h-[350px]">
            {stickSeriesLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="w-10 h-10 border-2 border-slate-200 border-t-[#006994] rounded-full animate-spin"></div>
              </div>
            ) : stickSeriesData.length === 0 ? (
              <div className="flex items-center justify-center h-full text-slate-500">
                <p>No data available for selected filters</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={stickSeriesData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="count"
                    nameKey="name"
                    isAnimationActive={false}
                    onClick={(data) => setSelectedSeries(data)}
                    label={({ name, percentage }) => percentage >= 4 ? `${name} ${percentage}%` : ''}
                    labelLine={({ percentage }) => percentage >= 4}
                  >
                    {stickSeriesData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={COLORS[index % COLORS.length]}
                        style={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<StickSeriesTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 mt-4 pt-4 border-t border-slate-100">
            {stickSeriesData.map((item, idx) => (
              <div 
                key={idx} 
                className="flex items-center gap-2 text-sm cursor-pointer hover:bg-blue-50 p-1.5 rounded transition-colors"
                onClick={() => setSelectedSeries(item)}
              >
                <div 
                  className="w-3 h-3 rounded-full shrink-0" 
                  style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                ></div>
                <span className="text-slate-600 truncate">{item.name}</span>
                <span className="text-slate-900 font-medium ml-auto">{item.percentage}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-100 mt-8">
        <div className="px-6 py-5 border-b border-slate-100">
          <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Category Trend
          </h3>
          <p className="text-sm text-slate-500 mt-1">Root cause categories over time</p>
        </div>
        <div className="p-6">
          {categoryTrendLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin h-8 w-8 border-2 border-[#006994] border-t-transparent rounded-full"></div>
            </div>
          ) : categoryTrend.length === 0 ? (
            <div className="text-center py-16 text-slate-400">
              <p className="text-sm">No category trend data available</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={categoryTrend} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <Tooltip 
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }}
                  itemSorter={(item) => -item.value}
                />
                <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '16px' }} />
                {categoryNames.slice(0, 10).map((cat, i) => (
                  <Line
                    key={cat}
                    type="monotone"
                    dataKey={cat}
                    stroke={COLORS[i % COLORS.length]}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
          {categoryNames.length > 10 && (
            <p className="text-xs text-slate-400 text-center mt-2">Showing top 10 categories. {categoryNames.length - 10} more not shown.</p>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-100 mt-8" data-testid="installer-daily-trend-card">
        <div className="px-6 py-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Daily Fixes by Installer
            </h3>
            <p className="text-sm text-slate-500 mt-1">Number of service logs each day, stacked by installer · click a bar for the day's breakdown</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 font-medium">Window</span>
            <select
              data-testid="installer-trend-days"
              value={installerDays}
              onChange={(e) => setInstallerDays(parseInt(e.target.value, 10))}
              className="text-sm border border-slate-200 rounded-md px-2 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#006994]/30"
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
        </div>
        <div className="p-6">
          {installerTrendLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin h-8 w-8 border-2 border-[#006994] border-t-transparent rounded-full"></div>
            </div>
          ) : installerTrend.length === 0 ? (
            <div className="text-center py-16 text-slate-400">
              <p className="text-sm">No service logs in the selected window</p>
            </div>
          ) : (
            <>
            <ResponsiveContainer width="100%" height={380}>
              <BarChart data={installerTrend} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  stroke="#94a3b8"
                  tickFormatter={formatShortDate}
                  interval="preserveStartEnd"
                  minTickGap={16}
                />
                <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" allowDecimals={false} />
                <Tooltip
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }}
                  itemSorter={(item) => -item.value}
                  labelFormatter={(label) => `${formatShortDate(label)}, ${String(label).slice(0, 4)}`}
                  formatter={(value, name) => [`${value} fix${value === 1 ? '' : 'es'}`, name]}
                />
                {installerNames.map((name, i) => (
                  <Bar
                    key={name}
                    dataKey={name}
                    stackId="fixes"
                    fill={name === 'Other' ? '#94a3b8' : COLORS[i % COLORS.length]}
                    onClick={async (payload) => {
                      if (!payload || !payload.payload) return;
                      const row = payload.payload;
                      setSelectedDay({
                        date: row.date,
                        total: row.total,
                        breakdown: null,
                      });
                      try {
                        const res = await axios.get(
                          `${API}/installer-daily-breakdown`,
                          { params: { date: row.date }, withCredentials: true }
                        );
                        setSelectedDay({
                          date: res.data?.date || row.date,
                          total: res.data?.total ?? row.total,
                          breakdown: res.data?.breakdown || [],
                        });
                      } catch (err) {
                        console.error('Failed to load installer breakdown:', err);
                        setSelectedDay(prev => prev ? { ...prev, breakdown: [] } : prev);
                      }
                    }}
                    cursor="pointer"
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-1.5 mt-3" data-testid="installer-legend">
              {installerNames.map((name, i) => (
                <span key={name} className="inline-flex items-center gap-1.5 text-[11px] text-slate-600 whitespace-nowrap">
                  <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: name === 'Other' ? '#94a3b8' : COLORS[i % COLORS.length] }} />
                  {name}
                </span>
              ))}
            </div>
            </>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-100 mt-8">
        <div className="px-6 py-5 border-b border-slate-100">
          <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Mean Time to Repair (MTTR)
          </h3>
          <p className="text-sm text-slate-500 mt-1">Average days between service visits per device, by PA</p>
        </div>
        <div className="p-6">
          {mttrLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin h-8 w-8 border-2 border-[#006994] border-t-transparent rounded-full"></div>
            </div>
          ) : !mttrStats ? (
            <div className="text-center py-16 text-slate-400">
              <p className="text-sm">No MTTR data available</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-[#006994]/5 rounded-xl p-4 border border-[#006994]/10">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Overall MTTR</p>
                  <p className="text-3xl font-bold text-[#006994] mt-1">{mttrStats.overall_mttr} <span className="text-base font-normal">days</span></p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Devices (2+ visits)</p>
                  <p className="text-3xl font-bold text-slate-800 mt-1">{mttrStats.total_devices}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total Repair Gaps</p>
                  <p className="text-3xl font-bold text-slate-800 mt-1">{mttrStats.total_repairs}</p>
                </div>
              </div>

              {mttrStats.pa_breakdown.length > 0 && (
                <ResponsiveContainer width="100%" height={Math.max(300, mttrStats.pa_breakdown.length * 32)}>
                  <BarChart data={mttrStats.pa_breakdown} layout="vertical" margin={{ top: 5, right: 30, left: 120, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis type="number" tick={{ fontSize: 12 }} stroke="#94a3b8" label={{ value: 'Avg Days', position: 'insideBottom', offset: -5, fontSize: 12 }} />
                    <YAxis type="category" dataKey="pa" tick={{ fontSize: 11 }} stroke="#94a3b8" width={110} />
                    <Tooltip
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }}
                      formatter={(value, name) => {
                        if (name === 'avg_mttr') return [`${value} days`, 'Avg MTTR'];
                        return [value, name];
                      }}
                      labelFormatter={(label) => `PA: ${label}`}
                    />
                    <Bar dataKey="avg_mttr" fill="#006994" radius={[0, 4, 4, 0]} barSize={20} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Screens Went Offline (&lt;30 Days Since Fixed)</p>
          <p className="text-3xl font-bold text-red-600 mt-2">{recurringKPIs.offlineUnder30}</p>
          <p className="text-xs text-slate-400 mt-1">out of {recurringKPIs.totalRecurring} recurring venues</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Problem Breakdown (&lt;30 Days Since Fixed)</p>
          {recurringKPIs.categoryBreakdown.length === 0 ? (
            <p className="text-sm text-slate-400">No data</p>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {recurringKPIs.categoryBreakdown.map(cat => (
                <div key={cat.name}>
                  <div className="flex items-center justify-between text-xs mb-0.5">
                    <span className="text-slate-700 truncate mr-2" title={cat.name}>{cat.name}</span>
                    <span className="text-slate-500 whitespace-nowrap font-medium">{cat.count} ({cat.percentage}%)</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-1.5">
                    <div className="bg-orange-500 h-1.5 rounded-full" style={{ width: `${cat.percentage}%` }}></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-100 mt-8" data-testid="recurring-venues-table">
        <div className="px-6 py-5 border-b border-slate-100">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Recurring Offline Venues
              </h3>
              <p className="text-sm text-slate-500 mt-1">
                Devices that went offline again after being fixed
              </p>
            </div>
            <span className="text-sm text-slate-500 bg-slate-50 px-3 py-1.5 rounded-lg font-medium">
              {processedRecurringVenues.length} venues
            </span>
          </div>
          <div className="flex items-center gap-3 mt-4 flex-wrap">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search DD Code..."
                value={recurringSearch}
                onChange={(e) => { setRecurringSearch(e.target.value); setRecurringPage(1); }}
                className="pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006994]/20 focus:border-[#006994] w-52"
                data-testid="recurring-search-input"
              />
            </div>
            <div className="w-48">
              <MultiSelect
                options={recurringFilterOptions.pa}
                selected={recurringFilters.pa}
                onChange={(val) => { setRecurringFilters(f => ({ ...f, pa: val })); setRecurringPage(1); }}
                placeholder="PA"
              />
            </div>
            <div className="w-48">
              <MultiSelect
                options={recurringFilterOptions.fa}
                selected={recurringFilters.fa}
                onChange={(val) => { setRecurringFilters(f => ({ ...f, fa: val })); setRecurringPage(1); }}
                placeholder="FA"
              />
            </div>
            <div className="w-40">
              <MultiSelect
                options={recurringFilterOptions.state}
                selected={recurringFilters.state}
                onChange={(val) => { setRecurringFilters(f => ({ ...f, state: val })); setRecurringPage(1); }}
                placeholder="State"
              />
            </div>
            <div className="w-44">
              <MultiSelect
                options={recurringFilterOptions.city}
                selected={recurringFilters.city}
                onChange={(val) => { setRecurringFilters(f => ({ ...f, city: val })); setRecurringPage(1); }}
                placeholder="City"
              />
            </div>
            {(recurringFilters.pa.length > 0 || recurringFilters.fa.length > 0 || recurringFilters.state.length > 0 || recurringFilters.city.length > 0 || recurringSearch) && (
              <button
                onClick={() => { setRecurringFilters({ pa: [], fa: [], state: [], city: [] }); setRecurringSearch(""); setRecurringPage(1); }}
                className="text-xs text-red-500 hover:text-red-700 font-medium px-2 py-1"
              >
                Clear All
              </button>
            )}
          </div>
        </div>
        
        {recurringLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin h-8 w-8 border-2 border-[#006994] border-t-transparent rounded-full"></div>
          </div>
        ) : paginatedRecurringVenues.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <AlertTriangle className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No recurring venues found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    {[
                      { key: "dd_code", label: "DD Code" },
                      { key: "store_name", label: "Store Name" },
                      { key: "recent_offline_date", label: "Recent Offline Date" },
                      { key: "fixed_date", label: "Fixed Date" },
                      { key: "days_since_fixed", label: "Days Since Fixed" },
                      { key: "address", label: "Address" },
                      { key: "city", label: "City" },
                      { key: "state", label: "State" },
                      { key: "pa", label: "PA" },
                      { key: "fa", label: "FA" }
                    ].map(col => (
                      <th
                        key={col.key}
                        className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 cursor-pointer hover:text-slate-700 select-none whitespace-nowrap"
                        onClick={() => toggleRecurringSort(col.key)}
                      >
                        <span className="flex items-center gap-1">
                          {col.label}
                          <ArrowUpDown className={`w-3 h-3 ${recurringSort.field === col.key ? 'text-[#006994]' : 'text-slate-300'}`} />
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {paginatedRecurringVenues.map((venue, idx) => (
                    <tr key={venue.dd_code} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-4 py-3 font-medium text-[#006994] whitespace-nowrap">
                        <Link
                          to={`/logs?dd=${encodeURIComponent(venue.dd_code)}`}
                          data-testid={`recurring-dd-link-${venue.dd_code}`}
                          className="hover:underline focus:outline-none focus:underline"
                          title={`View service logs for ${venue.dd_code}`}
                        >
                          {venue.dd_code}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-slate-700 max-w-[180px] truncate" title={venue.store_name}>{venue.store_name || "—"}</td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{venue.recent_offline_date}</td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{venue.fixed_date}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                          venue.days_since_fixed <= 7 ? 'bg-red-100 text-red-700' :
                          venue.days_since_fixed <= 30 ? 'bg-orange-100 text-orange-700' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {venue.days_since_fixed} days
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600 max-w-[200px] truncate" title={venue.address}>{venue.address || "—"}</td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{venue.city || "—"}</td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{venue.state || "—"}</td>
                      <td className="px-4 py-3 text-slate-600 max-w-[140px] truncate" title={venue.pa}>{venue.pa || "—"}</td>
                      <td className="px-4 py-3 text-slate-600 max-w-[140px] truncate" title={venue.fa}>{venue.fa || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {recurringTotalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100">
                <p className="text-sm text-slate-500">
                  Showing {(recurringPage - 1) * recurringPageSize + 1}–{Math.min(recurringPage * recurringPageSize, processedRecurringVenues.length)} of {processedRecurringVenues.length}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setRecurringPage(p => Math.max(1, p - 1))}
                    disabled={recurringPage === 1}
                    className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-sm text-slate-600 px-3">Page {recurringPage} of {recurringTotalPages}</span>
                  <button
                    onClick={() => setRecurringPage(p => Math.min(recurringTotalPages, p + 1))}
                    disabled={recurringPage === recurringTotalPages}
                    className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {selectedSeries && createPortal(
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]" 
          onClick={() => setSelectedSeries(null)}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl p-6 max-w-lg w-full mx-4 max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-slate-900">{selectedSeries.name} Series</h3>
                <p className="text-sm text-slate-500">{selectedSeries.count} logs ({selectedSeries.percentage}%)</p>
              </div>
              <button 
                onClick={() => setSelectedSeries(null)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            
            {selectedSeries.models && selectedSeries.models.length > 0 && (
              <div className="mb-4 pb-4 border-b border-slate-100">
                <p className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                  <Cpu className="w-4 h-4" /> Device Models ({selectedSeries.models.length})
                </p>
                <div className="space-y-1">
                  {selectedSeries.models.map((model, idx) => {
                    const modelPct = selectedSeries.count > 0 ? Math.round((model.count / selectedSeries.count) * 100) : 0;
                    return (
                      <div key={idx} className="flex justify-between text-sm py-1 hover:bg-slate-50 px-2 rounded">
                        <span className="text-slate-600">{model.name}</span>
                        <span className="text-slate-900 font-medium whitespace-nowrap">{model.count} ({modelPct}%)</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {selectedSeries.categories && selectedSeries.categories.length > 0 && (
              <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                <p className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2 shrink-0">
                  <AlertTriangle className="w-4 h-4" /> All Problem Categories ({selectedSeries.categories.length})
                </p>
                <div className="flex-1 overflow-y-auto space-y-1 pr-2">
                  {selectedSeries.categories.map((cat, idx) => {
                    const catPct = selectedSeries.count > 0 ? Math.round((cat.count / selectedSeries.count) * 100) : 0;
                    return (
                      <div key={idx} className="flex justify-between text-sm py-1 hover:bg-slate-50 px-2 rounded">
                        <span className="text-slate-600">{cat.name}</span>
                        <span className="text-slate-900 font-medium whitespace-nowrap">{cat.count} ({catPct}%)</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {selectedRootCause && createPortal(
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]" 
          onClick={() => setSelectedRootCause(null)}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl p-6 max-w-lg w-full mx-4 max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-slate-900">{selectedRootCause.name}</h3>
                <p className="text-sm text-slate-500">{selectedRootCause.count} logs ({selectedRootCause.percentage}%)</p>
              </div>
              <button 
                onClick={() => setSelectedRootCause(null)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            
            {selectedRootCause.subcategories && selectedRootCause.subcategories.length > 0 && (
              <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                <p className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2 shrink-0">
                  <AlertTriangle className="w-4 h-4" /> All Categories ({selectedRootCause.subcategories.length})
                </p>
                <div className="flex-1 overflow-y-auto space-y-1 pr-2">
                  {selectedRootCause.subcategories.map((sub, idx) => {
                    const subPct = selectedRootCause.count > 0 ? Math.round((sub.count / selectedRootCause.count) * 100) : 0;
                    return (
                      <div key={idx} className="flex justify-between text-sm py-1 hover:bg-slate-50 px-2 rounded">
                        <span className="text-slate-600">{sub.name}</span>
                        <span className="text-slate-900 font-medium whitespace-nowrap">{sub.count} ({subPct}%)</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {selectedDay && createPortal(
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]"
          onClick={() => setSelectedDay(null)}
          data-testid="day-fix-modal"
        >
          <div
            className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4 max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-slate-900">{selectedDay.date}</h3>
                <p className="text-sm text-slate-500">Fix activity for this day</p>
              </div>
              <button
                onClick={() => setSelectedDay(null)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                data-testid="day-fix-modal-close"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4 pb-4 border-b border-slate-100">
              <div className="bg-[#006994]/5 border border-[#006994]/10 rounded-lg p-3">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Total fixes</p>
                <p className="text-2xl font-bold text-[#006994] mt-1">{selectedDay.total}</p>
              </div>
              <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Installers</p>
                <p className="text-2xl font-bold text-emerald-600 mt-1">
                  {selectedDay.breakdown ? selectedDay.breakdown.length : '—'}
                </p>
              </div>
            </div>

            <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
              <p className="text-sm font-medium text-slate-700 mb-2 shrink-0">By installer</p>
              {selectedDay.breakdown === null ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin h-6 w-6 border-2 border-[#006994] border-t-transparent rounded-full"></div>
                </div>
              ) : selectedDay.breakdown.length === 0 ? (
                <p className="text-sm text-slate-400 py-4">No installer activity recorded.</p>
              ) : (
                <div className="flex-1 overflow-y-auto space-y-1 pr-2">
                  {selectedDay.breakdown.map((row, idx) => {
                    const pct = selectedDay.total > 0 ? Math.round((row.count / selectedDay.total) * 100) : 0;
                    return (
                      <div key={idx} className="flex justify-between items-center text-sm py-1.5 hover:bg-slate-50 px-2 rounded">
                        <span className="text-slate-700">{row.name}</span>
                        <span className="text-slate-900 font-medium whitespace-nowrap">{row.count} <span className="text-slate-400 text-xs">({pct}%)</span></span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
